import React, { useRef, useState, useEffect } from 'react';
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import MicIcon from '@mui/icons-material/Mic';
import SubtitlesIcon from '@mui/icons-material/Subtitles';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { constants } from '../../utils/constants';
import { getCookie } from '../login/cookies';



export type AudioMode =
  | 'audio_recording_only'
  | 'script_display_only'
  | 'script_display_with_audio_recording';

export interface CustomisableMarker {
  index: number;
  title: string;
  value: string;
  duration_ms?: string;
}

export interface BackgroundNoise {
  index: number;
  link: string;
  type: string;
  is_repeat: 'yes' | 'no';
  /** local display name before upload resolves */
  _fileName?: string;
  _uploading?: boolean;
}

export interface AudioTag {
  index: number;
  title: string;
  value: string;
  link: string;
  type: string;
  _fileName?: string;
  _uploading?: boolean;
}

export interface PauseDuration {
  title: string;
  value: string;
  duration_seconds: string;
}

export interface AudioModeConfig {
  audioMode: AudioMode;
  scriptSourceContent?: string;
  allowUploadRecordedFile?: boolean;
  recordedFileMaxSize?: number | null;
  customisableMarkers?: CustomisableMarker[];
  defaultDurationMs?: string;
  backgroundNoise?: BackgroundNoise[];
  audioTags?: AudioTag[];
  playSoundBetweenUtterances?: boolean;
  utteranceSoundLink?: string;
  utteranceSoundType?: string;
  pauseDurations?: PauseDuration[];
}


const AUDIO_MODE_OPTIONS: { value: AudioMode; label: string; description: string }[] = [
  {
    value: 'audio_recording_only',
    label: 'Audio Recording Only',
    description: 'Expert records audio without any displayed script',
  },
  {
    value: 'script_display_only',
    label: 'Script Display Only',
    description: 'Expert reads a script; no recording required',
  },
  {
    value: 'script_display_with_audio_recording',
    label: 'Script Display with Audio Recording',
    description: 'Expert reads a script and records audio simultaneously',
  },
];

const AUDIO_SECTION_LABEL_SX = {
  fontFamily: 'Montserrat',
  fontSize: '13px',
  fontWeight: 700,
  color: '#10383D',
};

const AUDIO_HINT_SX = {
  fontFamily: 'Montserrat',
  fontSize: '11px',
  color: '#6b7280',
};


function makeMarker(index: number): CustomisableMarker {
  return { index, title: '', value: '', duration_ms: '' };
}

function makeNoise(index: number): BackgroundNoise {
  return { index, link: '', type: '', is_repeat: 'no' };
}

function makeAudioTag(index: number): AudioTag {
  return { index, title: '', value: '', link: '', type: '' };
}

function makePause(): PauseDuration {
  return { title: '', value: '', duration_seconds: '' };
}


async function uploadToCompany(file: File): Promise<string> {
  const formData = new FormData();
  formData.append('file', file);
  const response = await fetch(`${constants.API_BASE_URL}upload/company/`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${getCookie('token')}` },
    body: formData,
  });
  if (!response.ok) throw new Error(`Upload failed: ${response.status}`);
  const data = await response.json();
  const link = data?.file_url ?? data?.data?.file_url;
  if (!link) throw new Error('No file_url in upload response');
  return link;
}


interface RowWrapProps {
  children: React.ReactNode;
  onRemove: () => void;
}

const RowWrap: React.FC<RowWrapProps> = ({ children, onRemove }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    {children}
    <Tooltip title="Remove row">
      <IconButton
        size="small"
        onClick={onRemove}
        sx={{ color: '#9ca3af', '&:hover': { color: '#e53e3e' }, flexShrink: 0 }}
      >
        <DeleteIcon sx={{ fontSize: 15 }} />
      </IconButton>
    </Tooltip>
  </Box>
);

interface AddRowButtonProps {
  onClick: () => void;
  label?: string;
}

const AddRowButton: React.FC<AddRowButtonProps> = ({ onClick, label = 'Add row' }) => (
  <Box
    onClick={onClick}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      mt: 0.75,
      cursor: 'pointer',
      color: '#3D85C6',
      '&:hover': { color: '#2f6ba0' },
    }}
  >
    <AddCircleOutlineIcon sx={{ fontSize: 16 }} />
    <Typography sx={{ fontFamily: 'Montserrat', fontSize: '12px', fontWeight: 600 }}>
      {label}
    </Typography>
  </Box>
);

interface SectionHeaderProps {
  icon: React.ReactNode;
  label: string;
  hint?: string;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({ icon, label, hint }) => (
  <Box sx={{ mb: 1.25 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
      {icon}
      <Typography sx={AUDIO_SECTION_LABEL_SX}>{label}</Typography>
    </Box>
    {hint && <Typography sx={AUDIO_HINT_SX}>{hint}</Typography>}
  </Box>
);


interface AudioModeSettingsProps {
  initialConfig?: AudioModeConfig;
  onConfigChange: (config: AudioModeConfig) => void;
  columnNames?: any[];
  previousStages?: any[];
  initialScriptContent?: string;
  showErrors?: boolean;
}


const AudioModeSettings: React.FC<AudioModeSettingsProps> = ({
  initialConfig,
  onConfigChange,
  columnNames = [],
  previousStages = [],
  initialScriptContent = '',
  showErrors = false,
}) => {
  const [audioMode, setAudioMode] = useState<AudioMode>(
    initialConfig?.audioMode ?? 'audio_recording_only',
  );
  const [allowUpload, setAllowUpload] = useState<boolean>(
    initialConfig?.allowUploadRecordedFile ?? false,
  );
  const [recordedFileMaxSize, setRecordedFileMaxSize] = useState<string>(
    initialConfig?.recordedFileMaxSize != null ? String(initialConfig.recordedFileMaxSize) : '',
  );
  const [markers, setMarkers] = useState<CustomisableMarker[]>(
    initialConfig?.customisableMarkers?.length
      ? initialConfig.customisableMarkers
      : [makeMarker(0)],
  );
  const [bgNoises, setBgNoises] = useState<BackgroundNoise[]>(
    initialConfig?.backgroundNoise ?? [],
  );
  const [audioTags, setAudioTags] = useState<AudioTag[]>(
    initialConfig?.audioTags?.length ? initialConfig.audioTags : [makeAudioTag(0)],
  );
  const [playSoundBetweenUtterances, setPlaySoundBetweenUtterances] = useState<boolean>(
    initialConfig?.playSoundBetweenUtterances ?? false,
  );
  const [utteranceSoundLink, setUtteranceSoundLink] = useState<string>(
    initialConfig?.utteranceSoundLink ?? '',
  );
  const [utteranceSoundType, setUtteranceSoundType] = useState<string>(
    initialConfig?.utteranceSoundType ?? '',
  );
  const [utteranceSoundFileName, setUtteranceSoundFileName] = useState<string>('');
  const [utteranceSoundUploading, setUtteranceSoundUploading] = useState<boolean>(false);
  const [pauseDurations, setPauseDurations] = useState<PauseDuration[]>(
    initialConfig?.pauseDurations?.length ? initialConfig.pauseDurations : [makePause()],
  );
  const [defaultDurationMs, setDefaultDurationMs] = useState<string>(
    initialConfig?.defaultDurationMs ?? '',
  );


  const [scriptContent, setScriptContent] = useState<string>(
    initialConfig?.scriptSourceContent ?? initialScriptContent,
  );

  const bgFileRefs = useRef<(HTMLInputElement | null)[]>([]);
  const atFileRefs = useRef<(HTMLInputElement | null)[]>([]);
  const utteranceFileRef = useRef<HTMLInputElement | null>(null);


  useEffect(() => {
    const maxSizeParsed = recordedFileMaxSize !== '' ? Number(recordedFileMaxSize) : null;
    onConfigChange({
      audioMode,
      scriptSourceContent: scriptContent,
      allowUploadRecordedFile: allowUpload,
      recordedFileMaxSize: allowUpload ? maxSizeParsed : null,
      customisableMarkers: markers,
      defaultDurationMs,
      backgroundNoise: bgNoises,
      audioTags,
      playSoundBetweenUtterances,
      utteranceSoundLink: playSoundBetweenUtterances ? utteranceSoundLink : '',
      utteranceSoundType: playSoundBetweenUtterances ? utteranceSoundType : '',
      pauseDurations,
    });

  }, [audioMode, allowUpload, recordedFileMaxSize, scriptContent, markers, defaultDurationMs, bgNoises, audioTags, playSoundBetweenUtterances, utteranceSoundLink, utteranceSoundType, pauseDurations, onConfigChange]);


  const handleModeChange = (value: AudioMode) => {
    setAudioMode(value);
    setAllowUpload(false);
    setRecordedFileMaxSize('');
    setScriptContent('');
    setMarkers([makeMarker(0)]);
    setDefaultDurationMs('');
    setBgNoises([]);
    setAudioTags([makeAudioTag(0)]);
    setPlaySoundBetweenUtterances(false);
    setUtteranceSoundLink('');
    setUtteranceSoundType('');
    setUtteranceSoundFileName('');
    setPauseDurations([makePause()]);
  };

  // ── Customisable Markers ───────────────────────────────────────────────────

  const addMarker = () =>
    setMarkers((prev) => [...prev, makeMarker(prev.length)]);

  const removeMarker = (i: number) =>
    setMarkers((prev) =>
      prev.filter((_, idx) => idx !== i).map((m, idx) => ({ ...m, index: idx })),
    );

  const patchMarker = (i: number, patch: Partial<CustomisableMarker>) =>
    setMarkers((prev) => prev.map((m, idx) => (idx === i ? { ...m, ...patch } : m)));

  // ── Background Noise ───────────────────────────────────────────────────────

  const addBgNoise = () =>
    setBgNoises((prev) => [...prev, makeNoise(prev.length)]);

  const removeBgNoise = (i: number) =>
    setBgNoises((prev) =>
      prev.filter((_, idx) => idx !== i).map((n, idx) => ({ ...n, index: idx })),
    );

  const handleBgFileSelect = async (i: number, file: File | null) => {
    if (!file) return;
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    setBgNoises((prev) =>
      prev.map((n, idx) =>
        idx === i ? { ...n, _fileName: file.name, _uploading: true } : n,
      ),
    );
    try {
      const link = await uploadToCompany(file);
      setBgNoises((prev) =>
        prev.map((n, idx) =>
          idx === i ? { ...n, link, type: ext, _uploading: false } : n,
        ),
      );
    } catch {
      setBgNoises((prev) =>
        prev.map((n, idx) => (idx === i ? { ...n, _uploading: false } : n)),
      );
    }
  };

  const removeBgFile = (i: number) => {
    setBgNoises((prev) =>
      prev.map((n, idx) =>
        idx === i ? { ...n, link: '', type: '', _fileName: undefined } : n,
      ),
    );
    if (bgFileRefs.current[i]) bgFileRefs.current[i]!.value = '';
  };

  // ── Audio Tags ─────────────────────────────────────────────────────────────

  const addAudioTag = () =>
    setAudioTags((prev) => [...prev, makeAudioTag(prev.length)]);

  const removeAudioTag = (i: number) =>
    setAudioTags((prev) =>
      prev.filter((_, idx) => idx !== i).map((t, idx) => ({ ...t, index: idx })),
    );

  const patchAudioTag = (i: number, patch: Partial<AudioTag>) =>
    setAudioTags((prev) => prev.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));

  const handleAtFileSelect = async (i: number, file: File | null) => {
    if (!file) return;
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    patchAudioTag(i, { _fileName: file.name, _uploading: true });
    try {
      const link = await uploadToCompany(file);
      patchAudioTag(i, { link, type: ext, _uploading: false });
    } catch {
      patchAudioTag(i, { _uploading: false });
    }
  };

  const removeAtFile = (i: number) => {
    patchAudioTag(i, { link: '', type: '', _fileName: undefined });
    if (atFileRefs.current[i]) atFileRefs.current[i]!.value = '';
  };

  // ── Play Sound Between Utterances ──────────────────────────────────────────

  const handleUtteranceSoundSelect = async (file: File | null) => {
    if (!file) return;
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    setUtteranceSoundFileName(file.name);
    setUtteranceSoundUploading(true);
    try {
      const link = await uploadToCompany(file);
      setUtteranceSoundLink(link);
      setUtteranceSoundType(ext);
    } finally {
      setUtteranceSoundUploading(false);
    }
  };

  const removeUtteranceSound = () => {
    setUtteranceSoundLink('');
    setUtteranceSoundType('');
    setUtteranceSoundFileName('');
    if (utteranceFileRef.current) utteranceFileRef.current.value = '';
  };

  // ── Pause Durations ────────────────────────────────────────────────────────

  const addPause = () => setPauseDurations((prev) => [...prev, makePause()]);

  const removePause = (i: number) =>
    setPauseDurations((prev) => prev.filter((_, idx) => idx !== i));

  const patchPause = (i: number, patch: Partial<PauseDuration>) =>
    setPauseDurations((prev) => prev.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));

  // ── Shared ─────────────────────────────────────────────────────────────────

  const showScriptEditor =
    audioMode === 'script_display_only' ||
    audioMode === 'script_display_with_audio_recording';

  const scriptValue = (() => {
    const match = scriptContent.match(/\{([^}]+)\}/);
    return match ? match[1] : '';
  })();
  const scriptError = showErrors && showScriptEditor && !scriptValue;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>

      {/* ── Audio Mode selector ── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 3,
          pb: 2.5,
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        <Box sx={{ minWidth: 220 }}>
          <Typography sx={AUDIO_SECTION_LABEL_SX}>
            Audio Mode
            <Typography component="span" sx={{ color: '#e53e3e', fontWeight: 700, ml: 0.25 }}>
              *
            </Typography>
          </Typography>
          <Typography sx={{ ...AUDIO_HINT_SX, mt: 0.25 }}>Controls recording behaviour</Typography>
        </Box>

        <FormControl size="small" sx={{ minWidth: 320 }}>
          <InputLabel id="audio-mode-label">Select Audio Mode</InputLabel>
          <Select
            labelId="audio-mode-label"
            label="Select Audio Mode"
            value={audioMode}
            onChange={(e) => handleModeChange(e.target.value as AudioMode)}
          >
            {AUDIO_MODE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                <Box>
                  <Typography
                    sx={{ fontFamily: 'Montserrat', fontSize: '13px', fontWeight: 600 }}
                  >
                    {opt.label}
                  </Typography>
                  <Typography sx={{ fontFamily: 'Montserrat', fontSize: '11px', color: '#6b7280' }}>
                    {opt.description}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {audioMode !== 'audio_recording_only' && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1.5,
              py: 0.6,
              borderRadius: 2,
              backgroundColor:
                audioMode === 'script_display_only' ? '#E3F2FD' : '#E8F5E9',
              border: `1px solid ${audioMode === 'script_display_only' ? '#90CAF9' : '#A5D6A7'
                }`,
              alignSelf: 'center',
            }}
          >
            {audioMode === 'script_display_only' ? (
              <SubtitlesIcon sx={{ fontSize: 14, color: '#1565C0' }} />
            ) : (
              <MicIcon sx={{ fontSize: 14, color: '#2E7D32' }} />
            )}
            <Typography
              sx={{
                fontSize: '11px',
                fontFamily: 'Montserrat',
                fontWeight: 600,
                color: audioMode === 'script_display_only' ? '#1565C0' : '#2E7D32',
              }}
            >
              {audioMode === 'script_display_only' ? 'Script only' : 'Script + Audio'}
            </Typography>
          </Box>
        )}
      </Box>

      {/* ── Script Source Configuration ── */}
      {showScriptEditor && (
        <Box sx={{ pt: 2.5, pb: 2.5, borderBottom: '1px solid #f0f0f0' }}>
          <SectionHeader
            icon={<SubtitlesIcon sx={{ fontSize: 15, color: '#3D85C6' }} />}
            label="Script Source Configuration"
            hint="Select the task upload column whose value will be used as the script during task execution."
          />
          <FormControl size="small" sx={{ maxWidth: '50%', width: '100%' }} error={scriptError}>
            <InputLabel id="script-source-label">Select script</InputLabel>
            <Select
              labelId="script-source-label"
              label="Select script"
              value={scriptValue}
              onChange={(e) => setScriptContent(`{${e.target.value}}`)}
            >
              {columnNames.map((col: any) => (
                <MenuItem key={col.key} value={col.key}>
                  <Typography sx={{ fontFamily: 'Montserrat', fontSize: '13px' }}>{col.value}</Typography>
                </MenuItem>
              ))}
            </Select>
            {scriptError && <FormHelperText>Script source is required</FormHelperText>}
          </FormControl>
        </Box>
      )}

      {/* ── Allow Upload Recorded File (Script Display Only) ── */}
      {audioMode === 'script_display_only' && (
        <Box
          sx={{
            pt: 2,
            pb: 2,
            borderBottom: '1px solid #f0f0f0',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ minWidth: 220 }}>
              <Typography sx={AUDIO_SECTION_LABEL_SX}>Allow Upload Recorded File</Typography>
              <Typography sx={{ ...AUDIO_HINT_SX, mt: 0.25 }}>
                Experts can upload a recorded audio file
              </Typography>
            </Box>
            <Checkbox
              checked={allowUpload}
              onChange={(e) => {
                setAllowUpload(e.target.checked);
                if (!e.target.checked) setRecordedFileMaxSize('');
              }}
              color="primary"
            />
            {allowUpload && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    border: '1px solid #d1d5db',
                    borderRadius: 1.5,
                    overflow: 'hidden',
                    height: 32,
                    width: 160,
                    backgroundColor: '#fff',
                    '&:focus-within': { borderColor: '#3D85C6', boxShadow: '0 0 0 2px #3D85C620' },
                  }}
                >
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="e.g. 50"
                    value={recordedFileMaxSize}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw === '' || /^\d+$/.test(raw)) setRecordedFileMaxSize(raw);
                    }}
                    style={{
                      flex: 1,
                      border: 'none',
                      outline: 'none',
                      padding: '0 8px',
                      fontFamily: 'Montserrat',
                      fontSize: '12px',
                      color: '#10383D',
                      background: 'transparent',
                      minWidth: 0,
                    }}
                  />
                  <Box
                    sx={{
                      px: 1,
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      backgroundColor: '#f3f4f6',
                      borderLeft: '1px solid #d1d5db',
                      flexShrink: 0,
                    }}
                  >
                    <Typography sx={{ fontFamily: 'Montserrat', fontSize: '11px', fontWeight: 600, color: '#6b7280' }}>
                      MB
                    </Typography>
                  </Box>
                </Box>
                <Typography sx={{ fontFamily: 'Montserrat', fontSize: '10px', color: '#9ca3af', pl: 0.5 }}>
                  Leave blank for no limit
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* ── Script Display with Audio Recording — 4 sections ── */}
      {(audioMode === 'script_display_only' || audioMode === 'script_display_with_audio_recording') && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>

          {/* ── Section 1: Enter Tags (customisable_markers) ── */}
          <Box sx={{ pt: 2.5, pb: 2.5, borderBottom: '1px solid #f0f0f0' }}>
            <SectionHeader
              icon={<MicIcon sx={{ fontSize: 15, color: '#3D85C6' }} />}
              label="Enter Tags"
              hint="Define custom markers that will appear in the script editor (e.g. FAST, SLOW, LOUD)."
            />

            <Box sx={{ maxWidth: '70%' }}>
              <Box sx={{ display: 'flex', gap: 1, mb: 0.75 }}>
                {['Title', 'Value', 'Time Per Word (ms)'].map((h, idx) => (
                  <Typography
                    key={h}
                    sx={{
                      fontFamily: 'Montserrat',
                      fontSize: '10px',
                      fontWeight: 700,
                      color: '#9ca3af',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      flex: idx === 2 ? 0.6 : 1,
                      minWidth: idx === 2 ? 80 : 100,
                    }}
                  >
                    {h}
                  </Typography>
                ))}
                <Box sx={{ width: 32 }} />
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {markers.map((m, i) => (
                  <RowWrap key={i} onRemove={() => removeMarker(i)}>
                    <TextField
                      size="small"
                      placeholder="Title"
                      value={m.title}
                      onChange={(e) => patchMarker(i, { title: e.target.value.toUpperCase() })}
                      sx={{ flex: 1, minWidth: 100 }}
                      error={showErrors && !m.title.trim()}
                      helperText={showErrors && !m.title.trim() ? 'Required' : ''}
                    />
                    <TextField
                      size="small"
                      placeholder="Value"
                      value={m.value}
                      onChange={(e) => patchMarker(i, { value: e.target.value })}
                      sx={{ flex: 1, minWidth: 100 }}
                      error={showErrors && !m.value.trim()}
                      helperText={showErrors && !m.value.trim() ? 'Required' : ''}
                    />
                    <TextField
                      size="small"
                      placeholder="e.g. 100ms"
                      value={m.duration_ms ?? ''}
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (raw === '' || /^\d+$/.test(raw)) patchMarker(i, { duration_ms: raw });
                      }}
                      inputProps={{ inputMode: 'numeric' }}
                      sx={{ flex: 0.6, minWidth: 80 }}
                    />
                  </RowWrap>
                ))}
              </Box>
              <AddRowButton onClick={addMarker} label="Add tag" />
            </Box>

            {/* Default Utterance Time */}
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Box sx={{ minWidth: 220 }}>
                <Typography sx={AUDIO_SECTION_LABEL_SX}>Default Time Per Word(ms)</Typography>
                <Typography sx={{ ...AUDIO_HINT_SX, mt: 0.25 }}>
                  Applied when a tag has no individual duration set
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    border: '1px solid #d1d5db',
                    borderRadius: 1.5,
                    overflow: 'hidden',
                    height: 32,
                    width: 140,
                    backgroundColor: '#fff',
                    '&:focus-within': { borderColor: '#3D85C6', boxShadow: '0 0 0 2px #3D85C620' },
                  }}
                >
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="e.g. 100ms"
                    value={defaultDurationMs}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw === '' || /^\d+$/.test(raw)) setDefaultDurationMs(raw);
                    }}
                    style={{
                      flex: 1,
                      border: 'none',
                      outline: 'none',
                      padding: '0 8px',
                      fontFamily: 'Montserrat',
                      fontSize: '12px',
                      color: '#10383D',
                      background: 'transparent',
                      minWidth: 0,
                    }}
                  />
                  <Box
                    sx={{
                      px: 1,
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      backgroundColor: '#f3f4f6',
                      borderLeft: '1px solid #d1d5db',
                      flexShrink: 0,
                    }}
                  >
                    <Typography sx={{ fontFamily: 'Montserrat', fontSize: '11px', fontWeight: 600, color: '#6b7280' }}>
                      ms
                    </Typography>
                  </Box>
                </Box>
                <Typography sx={{ fontFamily: 'Montserrat', fontSize: '10px', color: '#9ca3af', pl: 0.5 }}>
                  Leave blank for no default
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* ── Section 2: Background Noise (background_noise) ── */}
          <Box sx={{ pt: 2.5, pb: 2.5, borderBottom: '1px solid #f0f0f0' }}>
            <SectionHeader
              icon={<UploadFileIcon sx={{ fontSize: 15, color: '#3D85C6' }} />}
              label="Background Noise"
              hint="Upload audio files that will play as background noise during recording."
            />

            <Box sx={{ maxWidth: '50%' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {bgNoises.map((n, i) => (
                  <Box key={i}>
                    <RowWrap onRemove={() => removeBgNoise(i)}>
                      <input
                        type="file"
                        accept="audio/*"
                        style={{ display: 'none' }}
                        ref={(el) => {
                          bgFileRefs.current[i] = el;
                        }}
                        onChange={(e) => handleBgFileSelect(i, e.target.files?.[0] ?? null)}
                      />
                      {n._uploading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flex: 1 }}>
                          <CircularProgress size={14} />
                          <Typography sx={AUDIO_HINT_SX}>Uploading…</Typography>
                        </Box>
                      ) : n.link ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              px: 1,
                              py: 0.4,
                              backgroundColor: '#f0f7ff',
                              borderRadius: 1.5,
                              border: '1px solid #c7d7f0',
                              flex: 1,
                              overflow: 'hidden',
                            }}
                          >
                            <UploadFileIcon sx={{ fontSize: 12, color: '#3D85C6', flexShrink: 0 }} />
                            <Typography
                              sx={{
                                fontFamily: 'Montserrat',
                                fontSize: '11px',
                                color: '#2c5282',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                flex: 1,
                              }}
                            >
                              {n._fileName ?? n.link}
                            </Typography>
                            <Typography
                              sx={{
                                fontFamily: 'Montserrat',
                                fontSize: '10px',
                                color: '#9ca3af',
                                flexShrink: 0,
                              }}
                            >
                              .{n.type}
                            </Typography>
                          </Box>
                          <Tooltip title="Replace file">
                            <IconButton
                              size="small"
                              onClick={() => {
                                removeBgFile(i);
                                bgFileRefs.current[i]?.click();
                              }}
                              sx={{ color: '#9ca3af', '&:hover': { color: '#3D85C6' } }}
                            >
                              <UploadFileIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : (
                        <Box
                          onClick={() => bgFileRefs.current[i]?.click()}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            px: 1.25,
                            py: 0.5,
                            backgroundColor: '#3D85C6',
                            color: '#fff',
                            borderRadius: 1.5,
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: '#2f6ba0' },
                          }}
                        >
                          <UploadFileIcon sx={{ fontSize: 13 }} />
                          <Typography
                            sx={{ fontFamily: 'Montserrat', fontSize: '11px', fontWeight: 600 }}
                          >
                            Upload audio file
                          </Typography>
                        </Box>
                      )}
                    </RowWrap>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={n.is_repeat === 'yes'}
                          onChange={(e) =>
                            setBgNoises((prev) =>
                              prev.map((item, idx) =>
                                idx === i
                                  ? { ...item, is_repeat: e.target.checked ? 'yes' as const : 'no' as const }
                                  : item,
                              ),
                            )
                          }
                        />
                      }
                      label={
                        <Typography sx={{ fontFamily: 'Montserrat', fontSize: '12px', color: '#374151' }}>
                          Play in Loop
                        </Typography>
                      }
                      sx={{ mt: 0.25, ml: 0.25 }}
                    />
                    {showErrors && !n.link && !n._uploading && (
                      <Typography sx={{ fontFamily: 'Montserrat', fontSize: '11px', color: '#e53e3e', mt: 0.25, ml: 0.5 }}>
                        Audio file is required
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
              {bgNoises.length < 1 && (
                <AddRowButton onClick={addBgNoise} label="Add background noise" />
              )}
            </Box>
          </Box>

          {/* ── Section 3: Audio Tags (audio_tags) ── */}
          <Box sx={{ pt: 2.5, pb: 2.5, borderBottom: '1px solid #f0f0f0' }}>
            <SectionHeader
              icon={<MicIcon sx={{ fontSize: 15, color: '#7E57C2' }} />}
              label="Audio Tags"
              hint="Define named audio cue tags with an associated audio file (e.g. Beep Tag 1)."
            />

            <Box sx={{ maxWidth: '70%' }}>
              <Box sx={{ display: 'flex', gap: 1, mb: 0.75 }}>
                {['Title', 'Value', 'Audio File'].map((h, idx) => (
                  <Typography
                    key={h}
                    sx={{
                      fontFamily: 'Montserrat',
                      fontSize: '10px',
                      fontWeight: 700,
                      color: '#9ca3af',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      flex: idx === 2 ? 0.6 : 1,
                      minWidth: idx === 2 ? 80 : 100,
                    }}
                  >
                    {h}
                  </Typography>
                ))}
                <Box sx={{ width: 32 }} />
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {audioTags.map((at, i) => (
                  <Box key={i}>
                    <RowWrap onRemove={() => removeAudioTag(i)}>
                      <TextField
                        size="small"
                        placeholder="Title"
                        value={at.title}
                        onChange={(e) => patchAudioTag(i, { title: e.target.value })}
                        sx={{ flex: 1, minWidth: 100 }}
                        error={showErrors && !at.title.trim()}
                        helperText={showErrors && !at.title.trim() ? 'Required' : ''}
                      />
                      <TextField
                        size="small"
                        placeholder="Value"
                        value={at.value}
                        onChange={(e) => patchAudioTag(i, { value: e.target.value })}
                        sx={{ flex: 1, minWidth: 100 }}
                        error={showErrors && !at.value.trim()}
                        helperText={showErrors && !at.value.trim() ? 'Required' : ''}
                      />
                      <input
                        type="file"
                        accept="audio/*"
                        style={{ display: 'none' }}
                        ref={(el) => {
                          atFileRefs.current[i] = el;
                        }}
                        onChange={(e) => handleAtFileSelect(i, e.target.files?.[0] ?? null)}
                      />
                      <Box sx={{ flex: 0.6, minWidth: 80 }}>
                        {at._uploading ? (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              minWidth: 0,
                            }}
                          >
                            <CircularProgress size={13} />
                            <Typography sx={AUDIO_HINT_SX}>Uploading…</Typography>
                          </Box>
                        ) : at.link ? (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              px: 1,
                              py: 0.35,
                              backgroundColor: '#f5f0ff',
                              borderRadius: 1.5,
                              border: '1px solid #d8c7f0',
                              minWidth: 0,
                              overflow: 'hidden',
                            }}
                          >
                            <UploadFileIcon sx={{ fontSize: 12, color: '#7E57C2', flexShrink: 0 }} />
                            <Typography
                              sx={{
                                fontFamily: 'Montserrat',
                                fontSize: '11px',
                                color: '#5b3fa0',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                flex: 1,
                              }}
                            >
                              {at._fileName ?? 'Uploaded'}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => removeAtFile(i)}
                              sx={{ p: 0, color: '#e53e3e', flexShrink: 0 }}
                            >
                              <DeleteIcon sx={{ fontSize: 12 }} />
                            </IconButton>
                          </Box>
                        ) : (
                          <Box
                            onClick={() => atFileRefs.current[i]?.click()}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.4,
                              px: 1,
                              py: 0.35,
                              backgroundColor: showErrors && !at.link ? '#FEE2E2' : '#7E57C2',
                              color: showErrors && !at.link ? '#e53e3e' : '#fff',
                              border: showErrors && !at.link ? '1px solid #e53e3e' : 'none',
                              borderRadius: 1.5,
                              cursor: 'pointer',
                              minWidth: 0,
                              '&:hover': { backgroundColor: showErrors && !at.link ? '#FECACA' : '#6a3fb5' },
                            }}
                          >
                            <UploadFileIcon sx={{ fontSize: 13 }} />
                            <Typography
                              sx={{ fontFamily: 'Montserrat', fontSize: '11px', fontWeight: 600 }}
                            >
                              Upload audio
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </RowWrap>
                    {showErrors && !at.link && !at._uploading && (
                      <Typography sx={{ fontFamily: 'Montserrat', fontSize: '11px', color: '#e53e3e', mt: 0.25, ml: 0.5 }}>
                        Audio file is required
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
              <AddRowButton onClick={addAudioTag} label="Add audio tag" />
            </Box>
          </Box>

          {/* ── Section 4: Play Sound Between Utterances ── */}
          <Box sx={{ pt: 2.5, pb: 2.5, borderBottom: '1px solid #f0f0f0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: playSoundBetweenUtterances ? 2 : 0 }}>
              <Box sx={{ minWidth: 220 }}>
                <Typography sx={AUDIO_SECTION_LABEL_SX}>Audio before Utterance Starts</Typography>
                <Typography sx={{ ...AUDIO_HINT_SX, mt: 0.25 }}>
                  Upload an audio file to play Audio at the utterance start.
                </Typography>
              </Box>
              <Checkbox
                checked={playSoundBetweenUtterances}
                onChange={(e) => {
                  setPlaySoundBetweenUtterances(e.target.checked);
                  if (!e.target.checked) removeUtteranceSound();
                }}
                color="primary"
              />
            </Box>

            {playSoundBetweenUtterances && (
              <Box sx={{ maxWidth: '50%' }}>
                <input
                  type="file"
                  accept="audio/*"
                  style={{ display: 'none' }}
                  ref={utteranceFileRef}
                  onChange={(e) => handleUtteranceSoundSelect(e.target.files?.[0] ?? null)}
                />
                {utteranceSoundUploading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <CircularProgress size={14} />
                    <Typography sx={AUDIO_HINT_SX}>Uploading…</Typography>
                  </Box>
                ) : utteranceSoundLink ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        px: 1,
                        py: 0.4,
                        backgroundColor: '#f0f7ff',
                        borderRadius: 1.5,
                        border: '1px solid #c7d7f0',
                        flex: 1,
                        overflow: 'hidden',
                      }}
                    >
                      <UploadFileIcon sx={{ fontSize: 12, color: '#3D85C6', flexShrink: 0 }} />
                      <Typography
                        sx={{
                          fontFamily: 'Montserrat',
                          fontSize: '11px',
                          color: '#5b3fa0',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: 1,
                        }}
                      >
                        {utteranceSoundFileName || 'Uploaded'}
                      </Typography>
                    </Box>
                    <Tooltip title="Remove file">
                      <IconButton
                        size="small"
                        onClick={removeUtteranceSound}
                        sx={{ color: '#9ca3af', '&:hover': { color: '#e53e3e' } }}
                      >
                        <DeleteIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ) : (
                  <Box
                    onClick={() => utteranceFileRef.current?.click()}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      px: 1.25,
                      py: 0.5,
                      backgroundColor: '#3D85C6',
                      color: '#fff',
                      borderRadius: 1.5,
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#2f6ba0' },
                    }}
                  >
                    <UploadFileIcon sx={{ fontSize: 13 }} />
                    <Typography sx={{ fontFamily: 'Montserrat', fontSize: '11px', fontWeight: 600 }}>
                      Upload audio file
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>

          {/* ── Section 5: Pause Duration (pause_duration) ── */}
          <Box sx={{ pt: 2.5 }}>
            <SectionHeader
              icon={<MicIcon sx={{ fontSize: 15, color: '#F59E0B' }} />}
              label="Pause Duration"
              hint="Define named pause markers with a duration."
            />

            <Box sx={{ maxWidth: '70%' }}>
              <Box sx={{ display: 'flex', gap: 1, mb: 0.75 }}>
                {['Title', 'Value', 'Duration Seconds'].map((h, idx) => (
                  <Typography
                    key={h}
                    sx={{
                      fontFamily: 'Montserrat',
                      fontSize: '10px',
                      fontWeight: 700,
                      color: '#9ca3af',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      flex: idx === 2 ? 0.6 : 1,
                      minWidth: idx === 2 ? 80 : 100,
                    }}
                  >
                    {h}
                  </Typography>
                ))}
                <Box sx={{ width: 32 }} />
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {pauseDurations.map((p, i) => (
                  <RowWrap key={i} onRemove={() => removePause(i)}>
                    <TextField
                      size="small"
                      placeholder="Title"
                      value={p.title}
                      onChange={(e) => patchPause(i, { title: e.target.value.toUpperCase() })}
                      sx={{ flex: 1, minWidth: 100 }}
                      error={showErrors && !p.title.trim()}
                      helperText={showErrors && !p.title.trim() ? 'Required' : ''}
                    />
                    <TextField
                      size="small"
                      placeholder="<PAUSE_1>"
                      value={p.value}
                      onChange={(e) => patchPause(i, { value: e.target.value })}
                      sx={{ flex: 1, minWidth: 100 }}
                      error={showErrors && !p.value.trim()}
                      helperText={showErrors && !p.value.trim() ? 'Required' : ''}
                    />
                    <TextField
                      size="small"
                      placeholder="e.g. 2"
                      value={p.duration_seconds}
                      onChange={(e) => {
                        const intVal = e.target.value.replace(/\D/g, '');
                        patchPause(i, { duration_seconds: intVal });
                      }}
                      inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                      sx={{ flex: 0.6, minWidth: 80 }}
                      error={showErrors && !p.duration_seconds.trim()}
                      helperText={showErrors && !p.duration_seconds.trim() ? 'Required' : ''}
                    />
                  </RowWrap>
                ))}
              </Box>
              <AddRowButton onClick={addPause} label="Add pause" />
            </Box>
          </Box>

        </Box>
      )}
    </Box>
  );
};

export default AudioModeSettings;
